import {
  HELP_OPERATIONS_MAX_ENVIRONMENTS,
  HELP_OPERATIONS_MAX_PROCESSES,
  type HelpOperationsContext,
  type OperationsDashboardSnapshot,
} from '@tcvs/shared';

const CONTEXT_EVENT = 'tcvs:operations-help-context';
const OPEN_EVENT = 'tcvs:open-help';

let latestContext: HelpOperationsContext | undefined;

export function buildOperationsHelpContext(
  snapshot: OperationsDashboardSnapshot,
): HelpOperationsContext {
  return {
    observedAt: snapshot.generatedAt,
    environments: snapshot.environments.slice(0, HELP_OPERATIONS_MAX_ENVIRONMENTS).map((item) => ({
      stage: item.stage,
      health: item.health,
      metricsWindowHours: item.metricsWindowHours,
      apiRequests: item.apiRequests,
      serviceErrors: item.apiServerErrors + item.lambdaErrors + item.applicationErrors,
      lambdaThrottles: item.lambdaThrottles,
      dynamodbThrottles: item.dynamodbThrottles,
      queueVisibleMessages: item.queueVisibleMessages,
      queueOldestMessageSeconds: item.queueOldestMessageSeconds,
      deadLetterMessages: item.deadLetterMessages,
      activeAlarmCount: item.alarms.filter((alarm) => alarm.state === 'ALARM').length,
      unresolvedEbayExceptions: item.business.unresolvedEbayExceptions ?? 0,
      ...(item.business.ebayInventoryUpdatedAt === undefined
        ? {}
        : { ebayInventoryUpdatedAt: item.business.ebayInventoryUpdatedAt }),
      ...(item.business.ebayOrdersUpdatedAt === undefined
        ? {}
        : { ebayOrdersUpdatedAt: item.business.ebayOrdersUpdatedAt }),
      ...(item.cost?.netUsd === undefined ? {} : { monthToDateUsd: item.cost.netUsd }),
      ...(item.cost?.forecastMonthEndUsd === undefined
        ? {}
        : { forecastMonthEndUsd: item.cost.forecastMonthEndUsd }),
      ...(item.cost === undefined ? {} : { costStale: item.cost.stale }),
      topProcesses: item.activeProcesses.slice(0, HELP_OPERATIONS_MAX_PROCESSES).map((process) => ({
        name: process.name,
        invocations: process.invocations,
        errors: process.errors,
        throttles: process.throttles,
        averageDurationMs: process.averageDurationMs,
      })),
    })),
  };
}

export function publishOperationsHelpContext(context: HelpOperationsContext | undefined): void {
  latestContext = context;
  window.dispatchEvent(
    new CustomEvent<HelpOperationsContext | undefined>(CONTEXT_EVENT, { detail: context }),
  );
}

export function subscribeOperationsHelpContext(
  listener: (context: HelpOperationsContext | undefined) => void,
): () => void {
  const handle = (event: Event): void => {
    listener((event as CustomEvent<HelpOperationsContext | undefined>).detail);
  };
  window.addEventListener(CONTEXT_EVENT, handle);
  listener(latestContext);
  return () => window.removeEventListener(CONTEXT_EVENT, handle);
}

export function openOperationsHelp(): void {
  window.dispatchEvent(
    new CustomEvent<string>(OPEN_EVENT, {
      detail: 'Explain the current System operations dashboard status.',
    }),
  );
}

export function subscribeHelpOpen(listener: (question: string) => void): () => void {
  const handle = (event: Event): void => {
    listener((event as CustomEvent<string>).detail);
  };
  window.addEventListener(OPEN_EVENT, handle);
  return () => window.removeEventListener(OPEN_EVENT, handle);
}
